import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  MessageCircle,
  Inbox,
  ChevronRight,
  Loader2,
  AlertCircle,
  Check,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  History,
  ArrowDownToDot,
  ArrowUpFromLine,
  QrCode,
  Smartphone,
  Plug,
} from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { ScopePicker, ScopeBadge, type Scope } from "../components/ScopePicker";
import { TeachingBanner } from "../components/TeachingBanner";

/** WhatsApp reconciliation catalog — the top-level /whatsapp surface.
 *
 *  Mirrors /teams but scoped to WhatsApp. The first card streams
 *  WhatsApp messages from the last N days through the local LLM and
 *  surfaces anything where Waqas was either asked to do something or
 *  promised to do something AND there's no closure visible in the
 *  thread. Long-term Memso's Continuous Weave will reconcile across
 *  WhatsApp + Teams + email + calendar; for now this is WhatsApp-only.
 *
 *  Pairing: this page also hosts the QR pairing flow because pairing
 *  is the prerequisite for everything else. If the sidecar isn't
 *  paired yet we render a hero card with the QR + setup guidance and
 *  hide the scan catalog underneath. */

type WhatsAppCardId = "followup_scan";

interface WhatsAppCard {
  id: WhatsAppCardId;
  title: string;
  description: string;
  Icon: typeof MessageCircle;
  iconTile: string;
  cta: string;
}

const CATALOG: WhatsAppCard[] = [
  {
    id: "followup_scan",
    title: "Find WhatsApp messages awaiting your follow-up",
    description:
      "Scan past N days of WhatsApp chat and surface anything you were asked to do (or promised to do) where the thread doesn't show closure yet.",
    Icon: MessageCircle,
    iconTile: "bg-emerald-500 text-white ring-2 ring-emerald-300/40",
    cta: "Run scan",
  },
];

interface PairStatus {
  ok?: boolean;
  pair_status?: string;
  paired?: boolean;
  me?: { id: string; name: string | null } | null;
  qr_available?: boolean;
  last_qr_at?: string | null;
  last_connected_at?: string | null;
  last_disconnect_reason?: string | null;
}

interface ConnectorState {
  exists: boolean;
  scope_chosen: boolean;
  privacy_scope?: string | null;
  enabled?: boolean;
  /** True once a work_connector_run has been minted for this
   *  connector. The Baileys device can be paired while this is still
   *  false (QR scanned before the run was minted) — that was the
   *  silent-no-ingest bug. The setup card stays visible until this
   *  flips true so the connect action is always reachable. */
  run_minted?: boolean;
  run_id?: string | null;
  last_run_id?: string | null;
  last_run_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function WhatsApp() {
  const [active, setActive] = useState<WhatsAppCardId | null>(null);
  const [pair, setPair] = useState<PairStatus | null>(null);
  const [pairErr, setPairErr] = useState<string | null>(null);
  const [connector, setConnector] = useState<ConnectorState | null>(null);

  const refreshPair = async () => {
    try {
      const r = await fetch("/api/whatsapp/v1/pair/status", { credentials: "same-origin" });
      if (!r.ok) {
        setPairErr(`pair/status -> ${r.status}`);
        return;
      }
      const j = (await r.json()) as PairStatus;
      setPair(j);
      setPairErr(null);
    } catch (e) {
      setPairErr((e as Error).message);
    }
  };

  const refreshConnector = async () => {
    try {
      const r = await fetch("/api/whatsapp/connector", { credentials: "same-origin" });
      if (!r.ok) return;
      const j = (await r.json()) as ConnectorState;
      setConnector(j);
    } catch {
      // best effort, will retry on tick
    }
  };

  useEffect(() => {
    refreshPair();
    refreshConnector();
    const t = setInterval(() => {
      refreshPair();
      refreshConnector();
    }, 4_000);
    return () => clearInterval(t);
  }, []);

  const paired = pair?.paired === true;
  const scopeChosen = connector?.scope_chosen === true;
  const runMinted = connector?.run_minted === true;
  // "Connected" means BOTH halves are done: the Baileys device is
  // paired AND a Memso run has been minted (so the sidecar has a
  // bootstrap blob and can actually ship messages). Either half alone
  // ingests nothing. The original bug: the connect action lived only
  // inside the unpaired QR card, so pairing-before-minting stranded
  // the connector with no way back to the bootstrap action.
  const connected = paired && runMinted;
  const showScopePicker = !scopeChosen;
  const showSetup = scopeChosen && !connected;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            WhatsApp
          </div>
          {connector?.privacy_scope && <ScopeBadge scope={connector.privacy_scope} />}
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Reconcile your WhatsApp chat
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          On-demand checks against a local linked-device mirror of your WhatsApp. Read-only: the connector never marks chats read on your phone or sends messages.
        </p>
      </header>

      <TeachingBanner id="whatsapp_overview" />

      {pairErr && (
        <div className="glass rounded-xl border border-rose-300 dark:border-rose-700 backdrop-blur p-3 text-xs text-rose-700 dark:text-rose-300">
          Sidecar unreachable: {pairErr}. Make sure the <code className="bg-rose-50 dark:bg-rose-950/50 px-1 rounded">waki-whatsapp-connector</code> launchd job is running on this Mac.
        </div>
      )}

      {showScopePicker ? (
        <ScopePickerStep onSaved={refreshConnector} />
      ) : showSetup ? (
        <PairCard
          pair={pair}
          scope={connector?.privacy_scope ?? null}
          paired={paired}
          runMinted={runMinted}
          onConnected={() => {
            refreshPair();
            refreshConnector();
          }}
        />
      ) : active === "followup_scan" ? (
        <FollowupScanPanel onClose={() => setActive(null)} />
      ) : (
        <>
          <PairedHeader pair={pair} scope={connector?.privacy_scope ?? null} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CATALOG.map((card) => (
              <CatalogCard key={card.id} card={card} onRun={() => setActive(card.id)} />
            ))}
            <ComingSoonCard
              title="More reconciliation checks in flight"
              description="Dormant chats, unanswered group @-mentions, abandoned threads, voice-note triage, and cross-source closure (WhatsApp + Teams + email + calendar) land here as Memso's Continuous Weave matures."
            />
          </div>
          <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CacheHealthCard />
            <ScanHistoryCard />
          </div>
        </>
      )}
    </div>
  );
}

function ScopePickerStep({ onSaved }: { onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (scope: Scope) => {
    setBusy(true);
    setErr(null);
    const csrf = getCsrfToken();
    try {
      const r = await fetch("/api/whatsapp/connector/scope", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ privacy_scope: scope }),
      });
      const j = await r.json();
      if (!r.ok) {
        setErr(j.message || j.error || `scope save -> ${r.status}`);
        return;
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScopePicker
      subject="this WhatsApp account"
      busy={busy}
      error={err}
      onSubmit={submit}
      submitLabel="Save and continue to pairing"
      primaryOnly
      defaultScope="personal"
    />
  );
}

function PairedHeader({ pair, scope }: { pair: PairStatus | null; scope: string | null }) {
  if (!pair?.paired) return null;
  return (
    <div className="glass rounded-xl border border-emerald-200 dark:border-emerald-800 backdrop-blur p-3 flex items-center gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
        <Plug className="w-4 h-4" />
      </div>
      <div className="text-xs text-slate-700 dark:text-slate-200 min-w-0 flex-1 flex items-center gap-2 flex-wrap">
        <span>
          Paired as <span className="font-semibold">{pair.me?.name || pair.me?.id || "you"}</span>
          {pair.last_connected_at && <> since {new Date(pair.last_connected_at).toLocaleString()}</>}
        </span>
        {scope && <ScopeBadge scope={scope} />}
      </div>
    </div>
  );
}

function PairCard({
  pair,
  scope,
  paired,
  runMinted,
  onConnected,
}: {
  pair: PairStatus | null;
  scope: string | null;
  paired: boolean;
  runMinted: boolean;
  onConnected: () => void;
}) {
  const [bootBusy, setBootBusy] = useState(false);
  const [bootErr, setBootErr] = useState<string | null>(null);
  const [bootOk, setBootOk] = useState<string | null>(null);
  const [qrTick, setQrTick] = useState(0);

  // Poll the QR PNG by URL, busting the cache each tick. Re-renders
  // every ~3s so a new QR rotation lands automatically.
  useEffect(() => {
    const t = setInterval(() => setQrTick((x) => x + 1), 3_000);
    return () => clearInterval(t);
  }, []);

  const triggerBootstrap = async () => {
    setBootBusy(true);
    setBootErr(null);
    setBootOk(null);
    const csrf = getCsrfToken();
    try {
      const r = await fetch("/api/whatsapp/bootstrap", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify(scope ? { privacy_scope: scope } : {}),
      });
      const j = await r.json();
      if (!r.ok) {
        setBootErr(j.message || j.error || `bootstrap -> ${r.status}`);
        return;
      }
      setBootOk(`Connected. Run ${j.run_id?.slice(0, 12)}... is shipping messages to Memso.`);
      onConnected();
    } catch (e) {
      setBootErr((e as Error).message);
    } finally {
      setBootBusy(false);
    }
  };

  const isQrReady = !paired && pair?.pair_status === "qr" && pair?.qr_available;

  return (
    <section className="space-y-4">
      <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center ring-2 ring-emerald-300/40">
            <QrCode className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Connect WhatsApp to Memso</h2>
              {scope && <ScopeBadge scope={scope} />}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {paired
                ? "Your phone is linked. One tap left: connect it to Memso so messages start flowing."
                : "Two steps: scan the QR to link your phone, then connect it to Memso."}
              {pair?.last_disconnect_reason && !paired && (
                <> · last disconnect: <span className="font-mono">{pair.last_disconnect_reason}</span></>
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 inline-flex items-center justify-center">
            {paired ? (
              <div className="w-[240px] h-[240px] flex flex-col items-center justify-center gap-2 text-center px-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <Smartphone className="w-6 h-6" />
                </div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Linked as {pair?.me?.name || pair?.me?.id || "your phone"}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Device paired. No QR scan needed.
                </div>
              </div>
            ) : isQrReady ? (
              <img
                key={qrTick}
                src={`/api/whatsapp/v1/pair/qr.png?t=${qrTick}`}
                alt="WhatsApp pairing QR"
                width={240}
                height={240}
                className="block"
              />
            ) : (
              <div className="w-[240px] h-[240px] flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                {pair?.pair_status === "starting"
                  ? "Sidecar booting..."
                  : pair?.pair_status === "loggedOut"
                  ? "WhatsApp logged us out. Generating a fresh QR..."
                  : "Waiting for QR..."}
              </div>
            )}
          </div>

          <ol className="text-sm text-slate-700 dark:text-slate-200 space-y-3 list-decimal list-inside">
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              <span className="inline-flex items-start gap-2 align-top">
                <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                <span>
                  On your phone open <span className="font-semibold">WhatsApp → Settings → Linked Devices → Link a Device</span> and scan the QR.
                </span>
              </span>
            </li>
            <li className={paired && !runMinted ? "font-semibold" : ""}>
              <button
                type="button"
                onClick={triggerBootstrap}
                disabled={bootBusy}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
              >
                {bootBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
                Connect to Memso
              </button>
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                {paired
                  ? "Your phone is linked. Tap to finish connecting and start ingesting."
                  : "Mints the connector run and bootstraps the sidecar. Safe to tap before or after scanning."}
              </span>
              {bootOk && <div className="text-xs text-emerald-600 dark:text-emerald-300 mt-1">{bootOk}</div>}
              {bootErr && <div className="text-xs text-rose-600 dark:text-rose-300 mt-1">{bootErr}</div>}
            </li>
            <li className="text-xs text-slate-500 dark:text-slate-400">
              The sidecar persists auth state under <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">~/.waki-whatsapp-connector/auth-state/</code>; restarts will not re-prompt. Read-state contract: the connector never fires read receipts. Messages stay unread on your phone until you open them yourself.
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}

function CatalogCard({ card, onRun }: { card: WhatsAppCard; onRun: () => void }) {
  const { Icon } = card;
  return (
    <button
      type="button"
      onClick={onRun}
      className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 flex items-start gap-3 hover:border-emerald-400 dark:hover:border-emerald-500 transition-colors text-left"
    >
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center shadow-md ${card.iconTile}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{card.title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{card.description}</div>
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
          {card.cta}
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </button>
  );
}

function ComingSoonCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-4 flex items-start gap-3 bg-slate-50/50 dark:bg-slate-800/20">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 flex items-center justify-center">
        <Inbox className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</div>
      </div>
    </div>
  );
}

interface CacheStatsView {
  chats?: number;
  messages?: number;
  pending?: number;
  last_message_iso?: string | null;
  bootstrap_present?: boolean;
  pair_status?: string;
}

function CacheHealthCard() {
  const [stats, setStats] = useState<CacheStatsView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    fetch("/api/whatsapp/v1/stats", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        const d = (j?.data ?? j) as CacheStatsView;
        setStats(d);
      })
      .catch((e) => setErr(String(e?.message || e)));
  }, []);
  return (
    <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 flex items-start gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center ring-2 ring-emerald-300/40">
        <ArrowDownToDot className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Local mirror</div>
        {err ? (
          <div className="text-xs text-rose-600 dark:text-rose-300 mt-0.5">{err}</div>
        ) : stats ? (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {(stats.messages ?? 0).toLocaleString()} messages · {(stats.chats ?? 0).toLocaleString()} chats
            {(stats.pending ?? 0) > 0 && <> · {stats.pending} pending upload</>}
            {stats.last_message_iso && (
              <>
                <br />
                Last message: {new Date(stats.last_message_iso).toLocaleString()}
              </>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Loading...</div>
        )}
      </div>
    </div>
  );
}

interface ScanRunSummary {
  id: string;
  kind: string;
  status: string;
  input: { daysBack?: number; contextPrompt?: string | null };
  summary: { hits?: number; scanned?: number; total_ms?: number } | null;
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

function ScanHistoryCard() {
  const [runs, setRuns] = useState<ScanRunSummary[] | null>(null);
  useEffect(() => {
    fetch("/api/actions/whatsapp-scan-runs", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { runs: [] }))
      .then((j: { runs: ScanRunSummary[] }) => setRuns(j.runs ?? []))
      .catch(() => setRuns([]));
  }, []);
  const latest = runs?.[0];
  return (
    <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 flex items-start gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500 text-white flex items-center justify-center ring-2 ring-violet-300/40">
        <History className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recent scans</div>
        {runs == null ? (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Loading...</div>
        ) : latest ? (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Last run: {new Date(latest.started_at).toLocaleString()} · {latest.summary?.hits ?? 0} hits across {latest.summary?.scanned ?? 0} messages · {latest.input.daysBack}d window
          </div>
        ) : (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">No scans yet. Run one above.</div>
        )}
      </div>
    </div>
  );
}

interface WhatsAppScanHit {
  messageId: string;
  chatId: string;
  chatName: string;
  isGroup: boolean;
  fromJid: string;
  fromName: string;
  timestampIso: string;
  snippet: string;
  isSentByCurrentUser: boolean;
  needs_followup: boolean;
  closure_observed: boolean;
  kind: "asked_of_waqas" | "waqas_promised" | "neither";
  confidence: number;
  reason: string;
  suggested_action: string;
}

interface ScanProgress {
  scanned: number;
  total: number;
  prefiltered: number;
  hits: number;
}

type ScanPhase = "form" | "running" | "done" | "error";
type SortKey = "confidence" | "date" | "kind";

const PRESETS = [3, 7, 14, 30];

function FollowupScanPanel({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<ScanPhase>("form");
  const [daysBack, setDaysBack] = useState<number>(7);
  const [contextPrompt, setContextPrompt] = useState<string>("");

  const [hits, setHits] = useState<WhatsAppScanHit[]>([]);
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [preflightTotal, setPreflightTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [doneSummary, setDoneSummary] = useState<{
    total_hits: number;
    scanned: number;
    prefiltered: number;
    ai_calls: number;
    total_ms: number;
  } | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("confidence");
  const [handled, setHandled] = useState<Set<string>>(new Set());

  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef<string | null>(null);

  const startScan = async (e?: FormEvent) => {
    e?.preventDefault();
    setHits([]);
    setProgress(null);
    setError(null);
    setWarnings([]);
    setDoneSummary(null);
    setPreflightTotal(null);
    setPhase("running");

    const csrf = getCsrfToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (csrf) headers["X-CSRF-Token"] = csrf;

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const r = await fetch("/api/actions/scan-whatsapp-followups", {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: JSON.stringify({
          daysBack,
          contextPrompt: contextPrompt.trim() || undefined,
        }),
        signal: ctrl.signal,
      });
      if (!r.ok || !r.body) {
        const text = await r.text().catch(() => "");
        setError(`Request failed (${r.status}). ${text.slice(0, 200)}`);
        setPhase("error");
        return;
      }
      await consumeSse(r.body, {
        onEvent: (event, data) => {
          if (event === "start") {
            runIdRef.current = (data as { run_id?: string }).run_id ?? null;
          } else if (event === "preflight") {
            setPreflightTotal((data as { total: number }).total);
          } else if (event === "progress") {
            setProgress(data as ScanProgress);
          } else if (event === "hit") {
            const hit = data as WhatsAppScanHit;
            setHits((prev) => [...prev, hit]);
          } else if (event === "warn") {
            const msg = (data as { message?: string }).message;
            if (msg) setWarnings((prev) => [...prev.slice(-4), msg]);
          } else if (event === "error") {
            setError((data as { message?: string }).message || "Scan failed.");
            setPhase("error");
          } else if (event === "done") {
            setDoneSummary(
              data as {
                total_hits: number;
                scanned: number;
                prefiltered: number;
                ai_calls: number;
                total_ms: number;
              },
            );
            setPhase("done");
          }
        },
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setError(String((err as Error).message || err));
      setPhase("error");
    } finally {
      abortRef.current = null;
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    setPhase("form");
  };

  const reset = () => {
    setPhase("form");
    setHits([]);
    setProgress(null);
    setDoneSummary(null);
    setError(null);
    setWarnings([]);
    setHandled(new Set());
    runIdRef.current = null;
  };

  const sortedHits = useMemo(() => {
    const arr = hits.filter((h) => !handled.has(`${h.chatId}:${h.messageId}`));
    if (sortKey === "confidence") return [...arr].sort((a, b) => b.confidence - a.confidence);
    if (sortKey === "kind") {
      const order = (k: WhatsAppScanHit["kind"]) => (k === "asked_of_waqas" ? 0 : k === "waqas_promised" ? 1 : 2);
      return [...arr].sort((a, b) => order(a.kind) - order(b.kind) || b.confidence - a.confidence);
    }
    return [...arr].sort((a, b) => Date.parse(b.timestampIso) - Date.parse(a.timestampIso));
  }, [hits, handled, sortKey]);

  const markHandled = async (chatId: string, messageId: string) => {
    const composite = `${chatId}:${messageId}`;
    const csrf = getCsrfToken();
    const runId = runIdRef.current;
    setHandled((prev) => new Set(prev).add(composite));
    if (!runId) return;
    try {
      await fetch(`/api/actions/whatsapp-scan-runs/${encodeURIComponent(runId)}/handled`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ messageId: composite }),
      });
    } catch {
      // best effort
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
        >
          ← All WhatsApp checks
        </button>
        {phase === "running" && (
          <button
            type="button"
            onClick={cancel}
            className="text-xs font-semibold text-rose-600 dark:text-rose-300 hover:underline"
          >
            Cancel scan
          </button>
        )}
        {(phase === "done" || phase === "error") && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            <RefreshCw className="w-3 h-3" />
            Run another scan
          </button>
        )}
      </div>

      <header className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Find WhatsApp messages awaiting your follow-up
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Scans the last N days of locally-mirrored chat. Surfaces anything where you were asked to do something or promised to do something, and the thread doesn't show it being resolved.
        </p>
      </header>

      {phase === "form" ? (
        <form onSubmit={startScan} className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
              Look back days
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setDaysBack(p)}
                  className={
                    "rounded-full px-3 py-1 text-xs font-semibold transition-colors " +
                    (daysBack === p
                      ? "bg-emerald-500 text-white shadow-sm"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700")
                  }
                >
                  {p}d
                </button>
              ))}
              <input
                type="number"
                min={1}
                max={90}
                value={daysBack}
                onChange={(e) => setDaysBack(Math.max(1, Math.min(90, Number(e.target.value) || 0)))}
                className="w-20 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm"
              />
              <span className="text-xs text-slate-500 dark:text-slate-400">days</span>
            </div>
          </div>

          <div>
            <label htmlFor="ctx" className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
              Optional context
              <span className="ml-1 font-normal text-slate-500 dark:text-slate-400">
                (e.g. "only flag family chat replies" or "ignore work groups")
              </span>
            </label>
            <textarea
              id="ctx"
              rows={2}
              value={contextPrompt}
              onChange={(e) => setContextPrompt(e.target.value)}
              placeholder="Leave blank to flag every message that needs an action or response"
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
              maxLength={2000}
            />
          </div>

          <div className="pt-2 border-t border-slate-200/70 dark:border-slate-700/50 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Hard cap of 1,000 messages per scan. Concurrency 3 against the local model.
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 shadow-sm"
            >
              Run scan
            </button>
          </div>
        </form>
      ) : (
        <ScanRunningPanel
          phase={phase}
          progress={progress}
          preflightTotal={preflightTotal}
          warnings={warnings}
          error={error}
          summary={doneSummary}
          hits={sortedHits}
          allHitsCount={hits.length - handled.size}
          sortKey={sortKey}
          onSort={setSortKey}
          onHandled={markHandled}
        />
      )}
    </section>
  );
}

function ScanRunningPanel({
  phase, progress, preflightTotal, warnings, error, summary,
  hits, allHitsCount, sortKey, onSort, onHandled,
}: {
  phase: ScanPhase;
  progress: ScanProgress | null;
  preflightTotal: number | null;
  warnings: string[];
  error: string | null;
  summary: { total_hits: number; scanned: number; prefiltered: number; ai_calls: number; total_ms: number } | null;
  hits: WhatsAppScanHit[];
  allHitsCount: number;
  sortKey: SortKey;
  onSort: (s: SortKey) => void;
  onHandled: (chatId: string, messageId: string) => void;
}) {
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.scanned / progress.total) * 100))
    : 0;
  return (
    <div className="space-y-3">
      <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {phase === "running" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                Scanning {progress ? `${progress.scanned} of ${progress.total}` : "…"}
              </>
            ) : phase === "done" ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Done — {summary?.total_hits ?? 0} hit{summary?.total_hits === 1 ? "" : "s"} across {summary?.scanned ?? 0} message{summary?.scanned === 1 ? "" : "s"}
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-rose-500" />
                Scan failed
              </>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            {preflightTotal !== null && <span>Window total: {preflightTotal}</span>}
            {progress && progress.prefiltered > 0 && <span>· {progress.prefiltered} pre-filtered out</span>}
            {summary && (
              <span>
                · {(summary.total_ms / 1000).toFixed(1)}s ·{" "}
                {summary.scanned > 0 ? `${Math.round(summary.total_ms / summary.scanned)}ms/msg` : "—"}
              </span>
            )}
          </div>
        </div>
        {phase === "running" && (
          <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-200" style={{ width: `${pct}%` }} />
          </div>
        )}
        {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
        {warnings.length > 0 && (
          <details className="text-xs text-slate-500 dark:text-slate-400">
            <summary className="cursor-pointer">{warnings.length} warning{warnings.length === 1 ? "" : "s"}</summary>
            <ul className="mt-1 space-y-0.5 list-disc list-inside">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </details>
        )}
      </div>

      {hits.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 dark:text-slate-400">Sort:</span>
          {(["confidence", "kind", "date"] as SortKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onSort(k)}
              className={
                "rounded-full px-3 py-1 font-semibold capitalize " +
                (sortKey === k
                  ? "bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200")
              }
            >
              {k}
            </button>
          ))}
          <span className="text-slate-500 dark:text-slate-400">{allHitsCount} hit{allHitsCount === 1 ? "" : "s"}</span>
        </div>
      )}

      {hits.length > 0 ? (
        <ul className="space-y-2">
          {hits.map((hit) => (
            <HitRow
              key={`${hit.chatId}:${hit.messageId}`}
              hit={hit}
              onHandled={() => onHandled(hit.chatId, hit.messageId)}
            />
          ))}
        </ul>
      ) : phase === "done" ? (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 text-center">
          <Inbox className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-500" strokeWidth={2.5} />
          <h3 className="mt-3 text-lg font-bold text-slate-900 dark:text-slate-100">Nothing slipped through.</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
            No WhatsApp messages in the scanned window need a follow-up.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function HitRow({ hit, onHandled }: { hit: WhatsAppScanHit; onHandled: () => void }) {
  const tier = confidenceTier(hit.confidence);
  // WhatsApp Web doesn't have stable per-message deep-link semantics
  // for linked devices, so we link to the web app and let the user
  // click into the right chat. A future enhancement could open the
  // native app via the `whatsapp://` scheme on macOS.
  const deepLink = "https://web.whatsapp.com/";
  return (
    <li className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={"inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full " + tier.classes}>
            {tier.label} confidence
          </span>
          <KindBadge kind={hit.kind} />
          {hit.isGroup && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              group
            </span>
          )}
          <span className="text-[11px] text-slate-500 dark:text-slate-400">{formatDate(hit.timestampIso)}</span>
        </div>
        <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100 break-words">
          {hit.chatName || hit.chatId}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {hit.isSentByCurrentUser ? "You · " : ""}
          {hit.fromName || hit.fromJid || "(unknown sender)"}
        </div>
        {hit.snippet && (
          <div className="mt-1 text-xs text-slate-700 dark:text-slate-200 line-clamp-3">
            "{hit.snippet}"
          </div>
        )}
        {hit.reason && (
          <div className="mt-1 text-xs text-slate-700 dark:text-slate-200">{hit.reason}</div>
        )}
        {hit.suggested_action && (
          <div className="mt-0.5 text-xs italic text-slate-500 dark:text-slate-400">
            Suggested: {hit.suggested_action}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5"
          title="Open WhatsApp Web"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open WhatsApp
        </a>
        <button
          type="button"
          onClick={onHandled}
          className="inline-flex items-center gap-1 rounded-md bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white text-xs font-semibold px-2.5 py-1.5"
          title="Mark as handled — future scans skip this message"
        >
          <Check className="w-3.5 h-3.5" />
          Handled
        </button>
      </div>
    </li>
  );
}

function KindBadge({ kind }: { kind: WhatsAppScanHit["kind"] }) {
  if (kind === "asked_of_waqas") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200">
        <ArrowDownToDot className="w-3 h-3" />
        asked of you
      </span>
    );
  }
  if (kind === "waqas_promised") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
        <ArrowUpFromLine className="w-3 h-3" />
        you promised
      </span>
    );
  }
  return null;
}

function confidenceTier(c: number): { label: string; classes: string } {
  if (c >= 0.75) return { label: "high", classes: "bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200" };
  if (c >= 0.5) return { label: "medium", classes: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200" };
  return { label: "low", classes: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" };
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

interface SseHandlers {
  onEvent: (event: string, data: unknown) => void;
}

async function consumeSse(stream: ReadableStream<Uint8Array>, handlers: SseHandlers): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
      }
      if (data) {
        try {
          handlers.onEvent(event, JSON.parse(data));
        } catch {
          handlers.onEvent(event, data);
        }
      }
    }
  }
}
